import React, { useEffect, useState } from 'react';
import { Space, Tabs, TabsProps } from 'antd';
import ShopifyProductTable from '../components/ShopifyProduct/ShopifyProductTable/ShopifyProductTable';
import ShopifyShopDescription, { ShopifyShopActions, ShopifyShopStatus } from '../components/ShopifyShop/ShopifyShopDescription';
import ShopifyProductImageTable from '../components/ShopifyProductImage/ShopifyPorductImageTable/ShopifyProductImageTable';
import { PictureOutlined, ProductOutlined, ShopOutlined, TableOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector, useAuth } from '../store/hooks';
import { setSelectedShopifyShop, setShopifyShops, ShopifyShop } from '../store/shopifyShopSlice';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../config/firebase-config';

const ShopifyPage: React.FC = () => {
    const dispatch = useAppDispatch();
    const { currentUser, loading, claims } = useAuth();
    const userId = currentUser?.uid;
    const selectedCompany = useAppSelector((state) => state.companies.selectedCompany);
    const shopifyShops = useAppSelector((state) => state.shopifyShops.shopifyShops);
    const [activeTabKey, setActiveTabKey] = useState<string>("tables")
    const [dataTabKey, setDataTabKey] = useState<string>("products")

    useEffect(() => {
        if (loading) return;
        if (!userId) {
            console.error("User is not authenticated");
            return;
        }

        const hasWildcard = (claims?.companyAccess?.['*'] ?? 0) >= 10;
        const isSuperadmin = claims?.superadmin === true;

        const baseQuery = hasWildcard || isSuperadmin
            ? query(collection(db, "companies"), where("modules.shopify", "==", true))
            : query(
                collection(db, "companies"),
                where(`accessLevels.${userId}`, ">=", 10),
                where("modules.shopify", "==", true)
            );

        const unsubscribe = onSnapshot(baseQuery, (snapshot) => {
            const updatedShopifyShops = snapshot.docs
                .map(doc => {
                    const data = doc.data() as any;
                    if (!data?.modules?.shopify) return null;
                    const shopify = data.shopify || {};
                    const accessLevels = data.accessLevels || {};
                    return {
                        id: doc.id,
                        userIds: data.userIds || Object.keys(accessLevels),
                        shop: shopify.shop || data.name || '',
                        shopUrl: shopify.shopUrl,
                        status: shopify.status || 'created',
                        productCount: shopify.productCount ?? shopify.stats?.productCount,
                        iamgeCount: shopify.imageCount ?? shopify.stats?.imageCount,
                        llmLanguage: shopify.llmLanguage,
                        llmKeywords: shopify.llmKeywords,
                        llmTargetAudience: shopify.llmTargetAudience,
                        llmDescription: shopify.llmDescription,
                        shopifyTags: shopify.shopifyTags,
                        shopifyVendors: shopify.shopifyVendors,
                        shopifyProductTypes: shopify.shopifyProductTypes,
                        stats: shopify.stats,
                        updatedAt: data.updatedAt,
                        createdAt: data.createdAt,
                    } as ShopifyShop;
                })
                .filter(Boolean) as ShopifyShop[];
            dispatch(setShopifyShops(updatedShopifyShops));
        }, (error) => {
            console.error("Error fetching real-time updates: ", error);
        });

        return () => unsubscribe();
    }, [claims?.companyAccess, claims?.superadmin, dispatch, loading, userId]);

    useEffect(() => {
        if (selectedCompany?.modules?.shopify) {
            const data = selectedCompany as any;
            const shopify = data.shopify || {};
            const accessLevels = data.accessLevels || {};
            const selectedShop: ShopifyShop = {
                id: data.id,
                userIds: data.userIds || Object.keys(accessLevels),
                shop: shopify.shop || data.name || '',
                shopUrl: shopify.shopUrl,
                status: shopify.status || 'created',
                productCount: shopify.productCount ?? shopify.stats?.productCount,
                iamgeCount: shopify.imageCount ?? shopify.stats?.imageCount,
                llmLanguage: shopify.llmLanguage,
                llmKeywords: shopify.llmKeywords,
                llmTargetAudience: shopify.llmTargetAudience,
                llmDescription: shopify.llmDescription,
                shopifyTags: shopify.shopifyTags,
                shopifyVendors: shopify.shopifyVendors,
                shopifyProductTypes: shopify.shopifyProductTypes,
                stats: shopify.stats,
                updatedAt: data.updatedAt,
                createdAt: data.createdAt,
            };
            dispatch(setSelectedShopifyShop(selectedShop));
        } else if (shopifyShops.length > 0) {
            dispatch(setSelectedShopifyShop(shopifyShops[0]));
        } else {
            dispatch(setSelectedShopifyShop(null));
        }
    }, [dispatch, selectedCompany, shopifyShops]);

    const dataItems: TabsProps['items'] = [
        {
            key: 'products',
            label: <span>
                <ProductOutlined /> Products
            </span>,
            children: <ShopifyProductTable openImages={() => setDataTabKey("images")} />,
        },
        {
            key: 'images',
            label: <span>
                <PictureOutlined /> Images
            </span>,
            children: <ShopifyProductImageTable />
        },
    ];

    const topItems: TabsProps['items'] = [
        {
            key: 'details',
            label: (
                <span>
                    <ShopOutlined /> Shop Details
                </span>
            ),
            children: <ShopifyShopDescription />,
        },
        {
            key: 'tables',
            label: (
                <span>
                    <TableOutlined /> Data Tables
                </span>
            ),
            children: (
                <Tabs
                    onChange={(key) => setDataTabKey(key)}
                    activeKey={dataTabKey}
                    items={dataItems}
                />
            ),
        },
    ];

    return (
        <div style={{ padding: '20px' }}>
            <Space size="small" direction='vertical' style={{ width: "100%" }}>
                <Tabs
                    onChange={(activeKey) => setActiveTabKey(activeKey)}
                    activeKey={activeTabKey}
                    tabBarExtraContent={activeTabKey === 'details' ? (
                        <Space size="small">
                            <ShopifyShopStatus />
                            <ShopifyShopActions />
                        </Space>
                    ) : null}
                    items={topItems}
                />
            </Space>
        </div>
    );
};

export default ShopifyPage;
